<template>
  <div class="row" style="overflow: clip">
    <div class="col-md-3">
      <slot name="sidebar-before"></slot>
      <div class="card">
        <div class="card-body card-padding">
          <transition-group tag="ul" name="fade" class="nav nav-pills nav-stacked">
            <li
              v-for="dialogType in dialogTypes"
              :key="dialogType.key"
              :class="{ active: activeDialogType.data.key === dialogType.key }"
              @click="switchActiveDialogType(dialogType)"
            >
              <a href="#" @click.prevent>
                {{ dialogType.labelText }}
                <span class="label label-quiet m-l-10">
                  {{ loading ? '-' : getDialogTypeCount(dialogType) }}
                </span>
              </a>
              <hr v-if="dialogType.key === dialogTypes.unanswered.key" />
            </li>
          </transition-group>
        </div>
      </div>
      <slot name="sidebar-after"></slot>
    </div>
    <div class="col-md-9">
      <div class="row m-b-20">
        <div class="col-sm-8">
          <span key="explain" class="text-muted">
            {{ `${activeDialogType.data.infoText}:` }}
          </span>
        </div>
        <div class="col-sm-4">
          <div class="row tw-flex tw-items-center">
            <div class="col-xs-5">
              <div class="f-13 c-gray f-500 ucase text-right nowrap">
                {{ $t('Sort by') }}
              </div>
            </div>
            <div class="col-xs-7">
              <select v-model="orderBy.data" class="form-control f-13 bgm-white">
                <option value="relevance">{{ $t('Relevance') }}</option>
                <option value="last_message">
                  {{ $t('Latest messages') }}
                </option>
                <option value="incl_bids">{{ $t('Has bids') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <transition name="fade">
        <div
          v-show="
            activeDialogType.data === dialogTypes.shortlisted &&
            yjob.hasActiveContract &&
            shortlistedCount > 5 &&
            !loading
          "
          key="annoucement"
          class="card bgm-purple rounded m-b-5"
        >
          <div class="card-padding card-body p-t-10 p-b-15">
            <h4 class="c-white f-500">
              {{ $t('It looks like you found some great freelancers!') }}
              <button
                :form="`form-job-pause-${yjobId}`"
                type="submit"
                class="btn m-l-10 btn-default pull-right"
              >
                {{ $t('Pause job') }}
              </button>
            </h4>
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <BaseHtmlForm
              :id="`form-job-pause-${yjobId}`"
              :action="`/jobs/${yjobId}/pause`"
              method="post"
              hidden
              append-user-and-account
            />
            <p class="m-b-0 f-13 c-white">
              {{
                $t(
                  'If you do not wish to receive more conversations and' +
                    ' bids from other freelancers you can pause your job post now.',
                )
              }}
            </p>
          </div>
        </div>
      </transition>
      <div v-if="!loading && dialogs.length === 0" class="no-jobs alert alert-info p-10">
        <p>
          {{ activeDialogType.data.noResultText }}
        </p>
      </div>
      <transition-group
        :duration="{ enter: 600, leave: 600 }"
        name="list-complete"
        tag="div"
        class="m-t-20 p-relative"
      >
        <JobPageDialogsItemSkeleton v-if="loading" :key="'skeleton-1'" />
        <JobPageDialogsItemSkeleton v-if="loading" :key="'skeleton-2'" />

        <template v-if="!loading">
          <div v-for="dialog in dialogs" :key="dialog.localId" class="list-complete-item">
            <JobPageDialogsItem
              :dialog="dialog"
              :yjob="yjob"
              :job-rank-star-threshold="jobRankStarThreshold"
            ></JobPageDialogsItem>
          </div>
        </template>
      </transition-group>
    </div>
  </div>
</template>

<script lang="js">
import gql from "graphql-tag"
import JobPageDialogsItemSkeleton from '@/components/JobPageDialogsItemSkeleton.vue'
import JobPageDialogsItem from "@/components/JobPageDialogsItem.vue"
import JobPageDialogsItemHeader from '@/components/JobPageDialogsItemHeader.vue'
import JobPageDialogsItemFooter from '@/components/JobPageDialogsItemFooter.vue'
import JobPageDialogsItemMessage from '@/components/JobPageDialogsItemMessage.vue'
import { LocalStorageEncoder } from '@/utils/Encoders'
import { NetworkError } from '@/utils/notification'

const types = {
  unanswered: {
    key: 'unanswered',
    defaultOrderBy: 'relevance',
    labelText: $t('Unanswered', 1),
    infoText: $t('All unanswered conversations for this job'),
    noResultText: $t('You have no unanswered conversations or bids for this job.')
  },
  shortlisted: {
    key: 'shortlisted',
    defaultOrderBy: 'last_message',
    labelText: $t('Shortlisted'),
    infoText: $t('All shortlisted conversations for this job'),
    noResultText: $t('You have not yet shortlisted anyone for this job. Get started and explore the new conversations and bids you have received.')
  },
  fresh: {
    key: 'fresh',
    defaultOrderBy: 'relevance',
    labelText: $t('New'),
    infoText: $t('All new conversations for this job'),
    noResultText: $t('You have no new conversations or bids for this job.')
  },
  closed: {
    key: 'closed',
    defaultOrderBy: 'last_message',
    labelText: $t('Ended'),
    infoText: $t('All ended conversations for this job'),
    noResultText: $t('You have not yet ended any conversations or bids for this job.')
  },
}

export default {
  name: "JobPageDialogs",
  components: {
    JobPageDialogsItem,
    JobPageDialogsItemSkeleton,
  },
  props: {
    yjobId: {
      required: true,
      type: Number
    },
    jobRankStarThreshold: {
      required: true,
      type: Number
    },
    initialOrderBy: {
      type: String,
      default: null
    },
    initialType: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      activeDialogType: new LocalStorageEncoder( {
        data:types[ this.initialType ] ?? types.unanswered,
        queryPoint: 'type',
        encoder: (obj) => obj.key,
        decoder(str) { return types[str] },
      }),
      orderBy: new LocalStorageEncoder( {
        data:this.initialOrderBy || (types[ this.initialType ] ?? types.unanswered).defaultOrderBy,
        queryPoint: 'orderBy'
      }),
      noLoading: false,
      yjob: {},
      dialogTypes: types,
    }
  },
  apollo: {
    yjob: {
      query: gql`
        query YjobDialog($yjobId: Int!, $dialogOrderBy: DialogSort!)  {
          yjob(localId: $yjobId) {
            ...JobPageDialogsItemHeaderFragmentForYjob
            ...JobPageDialogsItemFooterFragmentForYjob
            globalId
            localId
            hasActiveContract
            dialogs(orderBy: $dialogOrderBy) {
              data {
                ...JobPageDialogsItemFragmentForDialog
                ...JobPageDialogsItemHeaderFragmentForDialog
                ...JobPageDialogsItemFooterFragmentForDialog
                ...JobPageDialogsItemMessageFragmentForDialog
                localId
                dialogType
                isUnanswered
              }
            }
          }
        }
        ${JobPageDialogsItem.fragments.dialog}
        ${JobPageDialogsItemHeader.fragments.dialog}
        ${JobPageDialogsItemFooter.fragments.dialog}
        ${JobPageDialogsItemMessage.fragments.dialog}
        ${JobPageDialogsItemHeader.fragments.yjob}
        ${JobPageDialogsItemFooter.fragments.yjob}
      `,
      variables() {
        return {
          yjobId: this.yjobId, // Required by JobPageDialogsItem.fragments.dialog
          dialogOrderBy: this.orderBy.data.toUpperCase()
        }
      },
      error(err) {
        if (err) {
          new NetworkError({
            msg: 'Error occurred. The action was not completed',
            ex: err
          }).dispatch(this)
        }
      }
    },
  },
  computed: {
    dialogs() {
      if (this.activeDialogType.data.key === 'unanswered') {
        return this.yjob.dialogs.data.filter(d => d.isUnanswered && d.dialogType.toLowerCase() !== types.closed.key)
      }
      return this.yjob.dialogs.data.filter(d => d.dialogType.toLowerCase() === this.activeDialogType.data.key)
    },
    unansweredCount() {
      return this.yjob?.dialogs?.data?.filter(d => d.isUnanswered && d.dialogType.toLowerCase() !== types.closed.key).length ?? 0
    },
    shortlistedCount() {
      return this.yjob?.dialogs?.data?.filter(d => d.dialogType.toLowerCase() === types.shortlisted.key).length ?? 0
    },
    freshCount() {
      return this.yjob?.dialogs?.data?.filter(d => d.dialogType.toLowerCase() === types.fresh.key).length ?? 0
    },
    closedCount() {
      return this.yjob?.dialogs?.data?.filter(d => d.dialogType.toLowerCase() === types.closed.key).length ?? 0
    },
    loading() {
      return this.$apollo.queries.yjob.loading && !this.noLoading
    }
  },
  methods: {
    getDialogTypeCount(dialogType) {
      return this[ `${dialogType.key}Count` ] ?? 0
    },
    switchActiveDialogType(dialogType) {
      if (this.activeDialogType.data.key !== dialogType.key) {
        this.activeDialogType.data = dialogType
      }
      if (!this.orderBy.data) {
        this.orderBy.data = dialogType.defaultOrderBy
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.list-complete-item {
  transition: all 0.6s;
}
.list-complete-enter-from,
.list-complete-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
.list-complete-leave-active {
  position: absolute;
  width: 100%;
  max-width: 100%;
  display: block;
}
</style>
